/***
 *
 *   USER AVATAR
 *   Image with an initials fallback. Ported from
 *   ui-reference/src/components/ui/avatar.tsx.
 *
 *   This replaces the `lettered-avatar` package for new code: that package
 *   seeds its own background colour per name, which produced off-brand fills
 *   no token sweep could reach. The fallback here is a fixed primary tint, as
 *   in the reference.
 *
 *   The existing `Avatar` export is a different thing — the organisation
 *   switcher in the sidebar — and is replaced in Phase 3.
 *
 *   PROPS
 *   src: image url (optional)
 *   name: full name, used to derive initials and the alt text
 *   initials: override the derived initials
 *   size: sm/md/lg (default: md)
 *   bordered: add a hairline, as the reference does in the top bar
 *
 **********/

import { useState } from 'react';
import { ClassHelper, cn } from 'components/lib';
import Style from './userAvatar.tailwind.js';

export function initialsFromName(name) {
    if (!name) return '?';
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    const compact = String(name).replace(/\s+/g, '');
    return (compact.slice(0, 2) || '?').toUpperCase();
}

export function UserAvatar(props) {
    // An <img> that 404s would otherwise leave a broken-image glyph where the
    // initials should be, so failures fall back explicitly.
    const [failed, setFailed] = useState(false);
    const initials = props.initials || initialsFromName(props.name);
    const showImage = props.src && !failed;

    const style = ClassHelper(Style, {
        [props.size || 'md']: true,
        bordered: props.bordered,
        className: props.className,
    });

    return (
        <span className={style}>
            {showImage ? (
                <img
                    src={props.src}
                    alt={props.name || ''}
                    className={Style.image}
                    onError={() => setFailed(true)}
                />
            ) : (
                <span className={cn(Style.fallback, props.fallbackClassName)} aria-hidden={!!props.name}>
                    {initials}
                </span>
            )}
        </span>
    );
}
